import { useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";

/**
 * RouteSeo — injects per-route <title>, <meta description>,
 * canonical, and Open Graph tags on every page.
 *
 * Fixes Google Search Console issues:
 *   • Duplicate without user-selected canonical
 *   • Alternate page with proper canonical tag
 *   • Crawled - currently not indexed (thin / duplicate meta)
 */

const ORIGIN = "https://funontherun.co";

const META = {
  "/": {
    title: "Fun On The Run Plus One — Meet real people through real activities",
    description:
      "Meet real people through real activities near you. Find friends, activity partners, and new experiences — without endless swiping. Live in Dallas, Fort Worth, Austin & New Orleans.",
  },
  "/app": {
    title: "Fun in my City — What's happening near you | FOTR",
    description:
      "Discover a live feed of activities, events, and meetups happening right now in Dallas, Fort Worth, Austin, and New Orleans. Powered by Ticketmaster, SeatGeek, and Eventbrite.",
  },
  "/community": {
    title: "Community & Partners — Dallas Mavericks, TCU, UT Dallas | FOTR",
    description:
      "See how Fun On The Run Plus One is building real community with the Dallas Mavericks, TCU, UT Dallas, and more. Ambassadors, referrals, and local events.",
  },
  "/concierge": {
    title: "AI Activity Concierge — Tell us your vibe, we'll find your night | FOTR",
    description:
      "Our GPT-powered concierge knows your city, your crew, and your interests. Skip the scroll — just ask.",
  },
  "/story": {
    title: "The Story Behind FOTR — Why we built Fun On The Run Plus One",
    description:
      "The story behind Fun On The Run Plus One: how founder Danny turned a simple truth into a platform that helps people show up for life — together.",
  },
  "/safety": {
    title: "Safety — Verified profiles, real-time background checks | FOTR",
    description:
      "Every FOTR member can complete a Persona-powered real-time ID and background check. You decide the verification level that matches your comfort.",
  },
  "/invest": {
    title: "Invest in FOTR — Building the activity-first social network",
    description:
      "Fun On The Run Plus One is reinventing how people connect through shared activities. Learn about our vision, traction, and investment opportunities.",
  },
  "/tcu": {
    title: "FOTR × TCU — Partnership & Horned Frog activities",
    description:
      "Fun On The Run Plus One is proud to partner with TCU. See the latest campus-adjacent activities in Fort Worth and join the Horned Frog community on FOTR.",
  },
  "/join": {
    title: "Join the FOTR Beta — iOS & Android TestFlight",
    description:
      "Join the Fun On The Run Plus One beta on iOS or Android. Early access, invite your crew, and start showing up for real activities near you.",
  },
  "/vendors": {
    title: "FOTR for Vendors — Local events, promotions & community reach",
    description:
      "Reach a growing community of activity-first members in Dallas, Fort Worth, Austin, and New Orleans. Partner with Fun On The Run Plus One.",
  },
};

const DEFAULT_META = META["/"];

export default function RouteSeo() {
  const { pathname } = useLocation();
  // Normalize trailing slash to canonical form
  const key = pathname.replace(/\/+$/, "") || "/";
  const meta = META[key] || DEFAULT_META;
  const canonical = `${ORIGIN}${key === "/" ? "/" : key}`;

  // Noindex the gated investor room — not meant for public search
  const robots = key === "/investor-room" ? "noindex, nofollow" : "index, follow, max-image-preview:large";

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonical} />
      <meta property="og:title" content={meta.title} />
      <meta property="og:description" content={meta.description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="Fun On The Run Plus One" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={meta.title} />
      <meta name="twitter:description" content={meta.description} />
    </Helmet>
  );
}
